import { makeVar } from "@apollo/client";

/**
 * Auth state machine.
 *
 * REFRESHING is the state that makes MONITOR-135 fixable. Before it
 * existed an in-flight token refresh had nowhere to live: every path
 * had to either stay LOGGED_IN and hope, or drop to a state WithAuth
 * treats as signed out — which unmounts the authenticated subtree and
 * throws the user's page state away.
 *
 * Legal transitions:
 *
 *   WAITING     -> LOGGED_IN | LOGGED_OUT | LOGGING_IN
 *   LOGGING_IN  -> LOGGED_IN | LOGGED_OUT
 *   LOGGED_IN   -> REFRESHING | LOGGING_OUT
 *   REFRESHING  -> LOGGED_IN   (refresh resolved)
 *               -> REFRESHING  (idempotent; concurrent 401s join one flight)
 *               -> LOGGED_OUT  (refresh failed TERMINALLY only)
 *   LOGGING_OUT -> LOGGED_OUT
 *   LOGGED_OUT  -> LOGGING_IN | LOGGED_IN
 *
 * Explicitly illegal, each of which was a live bug:
 *
 *   - LOGGED_IN -> LOGGED_OUT on a recoverable failure. Route through
 *     REFRESHING instead.
 *   - LOGGED_IN -> WAITING. WAITING is boot-only; re-entering it
 *     mid-session is what let a Logto SDK flag flip tear down a live
 *     session.
 *   - REFRESHING -> ERROR. ERROR is no longer a state the machine
 *     transitions *to*. It is a display concern of the sign-in
 *     surfaces, reached only via LOGGED_OUT, so a recoverable network
 *     blip can never surface "you have been signed out by the system".
 */
export const AUTH_STATE = {
  WAITING: 0,
  LOGGED_IN: 1,
  LOGGED_OUT: 2,
  LOGGING_OUT: 3,
  LOGGING_IN: 4,
  // Was LOGGED_IN, is revalidating the access token. The authenticated
  // tree stays mounted and the URL does not change.
  REFRESHING: 5,
  // Vestigial. Nothing transitions to ERROR any more: it used to double
  // as a machine state and a "tell the user they were signed out" flag,
  // and overloading it is how a recoverable network blip surfaced a
  // terminal-sounding message. The message now travels in `authError`.
  // Kept only so an external `=== AUTH_STATE.ERROR` comparison cannot
  // silently start matching a live state.
  ERROR: 100,
};

export const authState = makeVar(AUTH_STATE.WAITING);

/**
 * True while the app should keep rendering the authenticated tree.
 * REFRESHING counts: the user has not been signed out, we are merely
 * re-minting their access token underneath them.
 *
 * Use this rather than an inline `=== LOGGED_IN` anywhere the answer
 * means "may this user see the app". Reserve `=== LOGGED_IN` for
 * "has a *settled* signed-in session", e.g. the redirect off /login.
 */
export const isAuthenticatedState = (state) =>
  state === AUTH_STATE.LOGGED_IN || state === AUTH_STATE.REFRESHING;

export const landingPage = makeVar(undefined);

export const authError = makeVar("");

export const account = makeVar({});

/**
 * Where to send the user after they sign in again.
 *
 * Single source of truth, captured once at the moment we decide the
 * session is over, from the live location. It used to be derived three
 * incompatible ways (router state, a sessionStorage key written on
 * Login mount, and a '/' default), which is why an involuntary
 * sign-out could land the user on the home page rather than the page
 * they were on.
 *
 * Mirrored into sessionStorage because the terminal sign-out path
 * full-page-navigates through Logto's end_session endpoint, which
 * wipes every in-memory reactive var. Callback.js reads the
 * sessionStorage copy on the way back in.
 *
 * The key used to be `logto:returnTo`, squatting inside the SDK's own
 * sessionStorage namespace. That was safe in fact but not by
 * construction: @logto/browser 4.0.14 only ever addresses exact keys
 * of the form `logto:<appId>[:<item>]` and `logto_cache:<appId>` with
 * targeted removeItem, and never sweeps the prefix (checked in
 * browser/lib/storage.js and cache.js). Nothing in that behaviour is
 * promised to us, though, and the whole point of this key is to
 * survive a full-page round trip through the SDK's end_session
 * endpoint. Owning our own namespace removes the dependency on an SDK
 * implementation detail. MONITOR-182.
 */
export const RETURN_TO_STORAGE_KEY = "monitor:returnTo";

/**
 * The pre-MONITOR-182 key. Read as a fallback, and cleared alongside
 * the current one, so a user mid-session across the deploy is not sent
 * to '/' on their next sign-in. Delete this and the two reads of it one
 * release after the rename has shipped.
 */
export const LEGACY_RETURN_TO_STORAGE_KEY = "logto:returnTo";

export const returnTo = makeVar(null);

/**
 * Only ever navigate to a same-origin path: reject anything absolute
 * ("https://evil.test/x") or protocol-relative ("//evil.test/x") so a
 * crafted location cannot turn the post-login redirect into an open
 * redirect. A path must start with a single "/". Backslashes are
 * rejected outright: "\/evil.test" reaches the history API as a
 * same-origin path today, but the browsers' and the router's handling
 * of it is not something worth depending on.
 */
export const isSafeReturnPath = (path) =>
  typeof path === "string" &&
  path.startsWith("/") &&
  !path.startsWith("//") &&
  !path.includes("\\");

// Capturing one of these as the return target would bounce the user
// straight back out of the app after signing in.
const NON_RETURNABLE_PREFIXES = ["/login", "/callback"];

const isReturnable = (path) =>
  isSafeReturnPath(path) &&
  !NON_RETURNABLE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

/**
 * Record where the user was when their session ended. Call this at the
 * transition into LOGGED_OUT, never later: by the time /login renders,
 * the original location is gone.
 *
 * Unsafe or non-returnable paths are ignored rather than stored, so a
 * hostile `from` never reaches sessionStorage and cannot be picked up
 * by Callback.js after the redirect.
 */
export const captureReturnTo = (path) => {
  if (!isReturnable(path)) return false;
  returnTo(path);
  try {
    sessionStorage.setItem(RETURN_TO_STORAGE_KEY, path);
  } catch {
    // Storage unavailable (private mode, quota). The in-memory var
    // still covers the client-side sign-in path; only the full-page
    // Logto round trip loses the target, and that falls back to '/'.
  }
  return true;
};

/**
 * Read the captured return target. Non-destructive: Callback.js clears
 * the sessionStorage copy once it has actually navigated.
 */
export const readReturnTo = () => {
  const inMemory = returnTo();
  if (isReturnable(inMemory)) return inMemory;
  try {
    const stored = sessionStorage.getItem(RETURN_TO_STORAGE_KEY);
    if (isReturnable(stored)) return stored;
    const legacy = sessionStorage.getItem(LEGACY_RETURN_TO_STORAGE_KEY);
    if (isReturnable(legacy)) return legacy;
  } catch {
    // See captureReturnTo.
  }
  return null;
};

export const clearReturnTo = () => {
  returnTo(null);
  try {
    sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
    // Clear the old key too. Leaving it behind would let a stale target
    // resurface through the fallback read on a later sign-in.
    sessionStorage.removeItem(LEGACY_RETURN_TO_STORAGE_KEY);
  } catch {
    // See captureReturnTo.
  }
};
